<template>
  <div class="w-full flex justify-center" :class="dark ? 'bg-gray-80' : 'bg-gray-5'">
    <div
      :class="[
        'flex flex-col w-full lg:max-w-[1200px]',
        size === 'lg' ? 'py-24 lg:gap-32' : 'px-8 py-12 gap-16'
      ]"
    >
      <section class="flex flex-col items-start gap-6 lg:flex-row lg:justify-between">
        <div class="flex flex-col gap-4 text-small">
          <div
            class="flex flex-col gap-1 lg:flex-row lg:items-center lg:gap-4 flex-wrap"
            :class="dark ? 'text-gray-30' : 'text-gray-70'"
          >
            <div class="flex items-center gap-4">
              <p>{{ $t('footer.company_name') }}</p>
              <hr :class="hrClass" />
              <p>{{ $t('footer.representative') }}</p>
            </div>

            <div class="flex flex-col lg:flex-row lg:items-center lg:gap-4">
              <hr :class="[hrClass, hrHiddenClass]" />
              <p>{{ $t('footer.address') }}</p>

              <hr :class="[hrClass, hrHiddenClass]" />
              <p>{{ $t('footer.phone') }}</p>

              <hr :class="[hrClass, hrHiddenClass]" />
              <p>{{ $t('footer.email') }}</p>
            </div>
          </div>

          <p class="text-gray-50">{{ $t('footer.copyright') }}</p>
        </div>

        <Select
          :class="size === 'md' ? 'w-full' : ''"
          :size="size === 'lg' ? 'lg' : 'md'"
          type="white"
          v-model="selectedFamilySite"
          :buttons="familySiteOptions"
          :default-text="$t('footer.family_site')"
          :dark="dark"
        />
      </section>

      <section
        class="flex flex-col items-start gap-[15px] lg:flex-row lg:items-end lg:justify-between"
      >
        <img
          class="h-23 lg:h-29"
          :src="dark ? '/logo/logo-gray-dark.svg' : '/logo/logo-gray.svg'"
        />

        <div class="flex gap-4">
          <a
            v-for="link in socialLinks"
            :key="link.name"
            :href="link.url"
            target="_blank"
            class="w-16 h-16 lg:w-20 lg:h-20 flex items-center justify-center"
            @mouseover="hoveredSocialLink = link.name"
            @mouseleave="hoveredSocialLink = null"
          >
            <Icon
              v-if="link.icon"
              :icon="link.icon"
              :color="hoveredSocialLink === link.name ? (dark ? '#03C75A' : '#00B14F') : '#8B9892'"
              class="transition-2"
              width="20"
            />
            <img
              v-else-if="link.image"
              :src="hoveredSocialLink === link.name ? link.image.hovered : link.image.default"
              class="w-20"
            />
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from '@iconify/vue'
import Select from '../select/Select.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'

type Props = {
  size: 'md' | 'lg'
  dark?: boolean
}
const props = defineProps<Props>()

const { locale } = useI18n()

const selectedFamilySite = ref<string>()
const hoveredSocialLink = ref<string | null>(null)

watch(selectedFamilySite, (newValue) => {
  if (newValue) {
    switch (newValue) {
      case 'teamgrit':
        if (locale.value === 'ko') {
          window.open('https://www.teamgrit.kr/ko', '_blank')
        } else {
          window.open('https://www.teamgrit.kr/en', '_blank')
        }
        break
      case 'cobiz':
        window.open('https://www.cobiz.kr/', '_blank')
        break
      case 'coplay':
        if (locale.value === 'ko') {
          window.open('https://www.coplay.kr/ko', '_blank')
        } else {
          window.open('https://www.coplay.kr/en', '_blank')
        }
        break
    }
    selectedFamilySite.value = undefined
  }
})

const familySiteOptions = [
  { label: 'TeamGRIT', value: 'teamgrit' },
  { label: 'CoBiz', value: 'cobiz' },
  { label: 'CoPlay', value: 'coplay' }
]

const socialLinks = [
  {
    name: 'facebook',
    url: 'https://www.facebook.com/teamgrit.kr',
    icon: 'akar-icons:facebook-fill'
  },
  {
    name: 'instagram',
    url: 'https://www.instagram.com/teamgrit_official/',
    icon: 'streamline:instagram-solid'
  },
  {
    name: 'youtube',
    url: 'https://www.youtube.com/@teamgrit7381',
    icon: 'uil:youtube'
  },
  {
    name: 'linkedin',
    url: 'https://www.linkedin.com/company/teamgrit-kr',
    icon: 'fa6-brands:linkedin'
  },
  {
    name: 'naver-blog',
    url: 'https://blog.naver.com/teamgrit',
    image: {
      default: '/icons/naver-blog.svg',
      hovered: props.dark ? '/icons/naver-blog-dark-hovered.svg' : '/icons/naver-blog-hovered.svg',
      disabled: props.dark
        ? '/icons/naver-blog-dark-disabled.svg'
        : '/icons/naver-blog-disabled.svg'
    }
  }
]

const hrClass = computed(() => 'w-px h-8 border-0 ' + (props.dark ? 'bg-gray-70' : 'bg-gray-20'))
const hrHiddenClass = 'hidden lg:block'
</script>
